<template>
  <div class="teams-view-wrapper" data-cy="teams-view">
    <SButton
      type="button"
      variant="outline"
      size="icon"
      class="close-button rounded-full"
      aria-label="close"
      data-cy="teams-close-btn"
      @click.stop="close"
    >
      <SvgIcon
        :iconContent="closeIcon"
        iconAlt="close"
        iconStyle="width: 20px; height: auto;"
        class="close-icon"
      />
    </SButton>
    <div class="teams-container w-full h-full d-flex fl-j-c fl-a-c">
      <div :class="isLoading ? 'overlay w-full h-full' : ''"></div>

      <div
        class="left w-1/2 h-full d-flex fl-col fl-s-b"
        v-if="teamsArray.length"
      >
        <div class="swiper-wrapper fl-a-c">
          <swiper
            :modules="modules"
            :slides-per-view="6"
            :space-between="10"
            :centered-slides="false"
            :freeMode="true"
            :navigation="true"
            virtual
            ref="swiper"
            @swiper="setSwiperRef"
          >
            <swiper-slide
              v-for="(team, index) in teamsArray"
              :key="index"
              :virtualIndex="index"
              class="d-flex fl-s-b fl-a-c"
            >
              <div
                :class="`team d-flex fl-col fl-s-b ${
                  currentTeam === index ? 'selected' : ''
                }`"
                :data-cy="`teams-team-card-${index}`"
                @click.stop="selectTeam(index)"
              >
                <h3>{{ `${$t("teams.team")} ${index + 1}` }}</h3>
                <p class="f-small">
                  {{
                    `${team.roomPlayers} ${
                      team.roomPlayers > 1
                        ? $t("teams.players")
                        : $t("teams.player")
                    }`
                  }}
                </p>
                <p
                  class="f-small"
                  :style="{ color: team.paired ? '#00975F' : '#FF5630' }"
                  data-cy="teams-paired-badge"
                >
                  {{ team.paired ? $t("teams.paired") : $t("teams.unpaired") }}
                </p>
              </div>
              <SSeparator orientation="vertical" />
            </swiper-slide>
          </swiper>
        </div>
        <div class="bottom d-flex fl-col fl-s-b fl-a-c">
          <div class="w-full">
            <p class="pb-2.5 f-large">
              {{
                `${teamsArray[currentTeam].roomPlayers} ${
                  teamsArray[currentTeam].roomPlayers > 1
                    ? $t("teams.players")
                    : $t("teams.player")
                }`
              }}
            </p>
            <div>
              <div class="input-label w-full d-flex fl-s-b fl-a-c">
                <label>{{ $t("teams.name") }}</label
                ><span>{{ $t("custom-fields.mandatory").toUpperCase() }}</span>
              </div>
              <SInput
                v-model="form.name"
                :placeholder="$t('teams.placeholder')"
                class="mb-4"
                data-cy="teams-name-input"
              />

              <div class="input-label w-full d-flex fl-s-b fl-a-c">
                <label>{{ $t("teams.level") }}</label>
              </div>
              <SSelect v-model="form.level">
                <SSelectTrigger class="w-full" data-cy="teams-level-select">
                  <SSelectValue :placeholder="$t('teams.select-level')" />
                </SSelectTrigger>
                <SSelectContent>
                  <SSelectItem
                    v-for="item in levelOptions"
                    :key="item.value"
                    :value="item.value"
                  >
                    {{ item.label }}
                  </SSelectItem>
                </SSelectContent>
              </SSelect>
            </div>
          </div>
          <div class="buttons w-full d-flex fl-col fl-a-c gap-2">
            <SButton
              v-if="!teamsArray[currentTeam].paired"
              type="button"
              size="lg"
              class="w-full"
              :disabled="!form.name"
              data-cy="teams-scan-and-pair-btn"
              @click="scanAndPair()"
            >
              {{ $t("button.pair-team") }}
            </SButton>
            <SButton
              type="button"
              size="lg"
              class="w-full"
              :disabled="!teamsArray[currentTeam].paired"
              data-cy="teams-proceed-btn"
              @click="proceed"
            >
              {{ $t("button.save") }}
            </SButton>
          </div>
        </div>
      </div>
      <div class="right w-1/2 h-full">
        <div class="content d-flex fl-j-c fl-a-c fl-col f-large">
          <p v-if="isLoading">{{ $t("teams.pairing") }}</p>
          <p v-else>
            {{
              teamsArray[currentTeam]?.paired
                ? $t("teams.paired-successfully")
                : $t("teams.get-ready")
            }}
          </p>
          <div class="card-icon d-flex fl-col fl-s-b fl-a-c">
            <SvgIcon
              :iconContent="cardsIcon"
              iconAlt="card-icon"
              iconStyle="width: 200px; height: auto;"
            />
            <SvgIcon
              v-if="isLoading"
              :iconContent="arrowDownIcon"
              iconAlt="arrow-down"
              iconStyle="width: 152px; height: auto;"
            />
            <SvgIcon
              v-if="teamsArray[currentTeam]?.paired"
              :iconContent="successIcon"
              iconAlt="info"
              iconStyle="width: 80px; height: auto;"
              class="success-icon"
            />
          </div>
          <SButton
            v-if="teamsArray[currentTeam]?.paired"
            type="button"
            variant="ghost"
            class="unpair-button"
            data-cy="teams-unpair-btn"
            @click="unpair"
          >
            {{ $t("button.unpair") }}
          </SButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useI18n } from "vue-i18n";
import { defineComponent, ref, computed } from "vue";
import { ITeam, TeamLevel } from "@/interfaces/teams/teams";
import BookingModule from "@/store/modules/booking";
import { BookingStep } from "@/constants/booking";
import { createTeams } from "@/utils/vue-helpers/booking";
import { scanOrderTicketsBulk } from "@/utils/vue-helpers/order-scanning";
import cardsIcon from "@/assets/images/svg/cards.svg";
import closeIcon from "@/assets/images/svg/close.svg";
import successIcon from "@/assets/images/svg/round-check.svg";
import arrowDownIcon from "@/assets/images/svg/arrow-down.svg";

import { postPairTeam, patchPairTeam, deleteTeam } from "@/api/teams/teams";
import Timeout from "await-timeout";
import { getBookingWithUUID } from "@/api/gql/product-slot-boooking";
import { attachedVueElement, detachVueElement } from "@/utils/helpers/scanner";
import ScannerModule from "@/store/modules/scanner";
import { watch } from "vue";
import { fetchBookingTeams } from "@/utils/helpers/teams";
import { LogService } from "@/services/logging";
import { cloneDeep, debounce, isEqual } from "lodash";
import NotificationService from "@/services/notification.service";
import AppModule from "@/store/modules/app";
import { IProductSlotBooking } from "@/types/db/product-slot-booking";
import { ScanningStatus } from "@/types/db/product-slot-state";
import { nowDB } from "@/utils/helpers/dates";
import UserModule from "@/store/modules/user";
import POSConfigModule from "@/store/modules/pos-config";
import { Virtual, FreeMode, Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/scss"; // core Swiper
import "swiper/scss/navigation"; // Navigation module
import "swiper/scss/pagination"; // Pagination module

import {
  SButton,
  SSeparator,
  SInput,
  SSelect,
  SSelectContent,
  SSelectItem,
  SSelectTrigger,
  SSelectValue,
} from "@smeetz/ui-core";

let swiper: any = null;
export default defineComponent({
  name: "Teams",
  components: {
    Swiper,
    SwiperSlide,
    SButton,
    SSeparator,
    SInput,
    SSelect,
    SSelectContent,
    SSelectItem,
    SSelectTrigger,
    SSelectValue,
  },
  setup() {
    const { t } = useI18n({});
    const teamsArray = ref<ITeam[]>([]);
    const currentTeam = ref(0);
    const isLoading = ref(false);
    const bookingId = ref();
    const bookingToken = ref();
    function setSwiperRef(swp: any) {
      swiper = swp;
    }

    const form = ref({
      name: "",
      level: TeamLevel.MEDIUM,
    });

    const levelOptions = [
      { label: t("teams.level-easy"), value: TeamLevel.EASY },
      { label: t("teams.level-medium"), value: TeamLevel.MEDIUM },
      { label: t("teams.level-hard"), value: TeamLevel.HARD },
    ];

    async function validateForm() {
      return !!form.value.name;
    }

    const isVisible = computed(() => {
      return BookingModule.bookingStep === BookingStep.Teams;
    });

    const scannerTog = computed(() => {
      return ScannerModule.tog;
    });
    async function waitForNfcTag(): Promise<string> {
      return new Promise((resolve) => {
        const nfcTagWatcher = watch(
          scannerTog,
          () => {
            console.log("watcher triggered: ", ScannerModule.value);
            console.log("scanned: ", ScannerModule.value);
            LogService.info(
              `Teams: scanner watcher triggered, value = ${ScannerModule.value}`
            );
            if (ScannerModule.value) {
              nfcTagWatcher(); // Remove the watcher
              resolve(ScannerModule.value);
            }
          },
          { deep: true }
        );
      });
    }

    const nfcTag = ref("");

    // the following array will contain all the teams that should be cancelled
    const cancelledTeams = ref<ITeam[]>([]);

    const bookingCopy = ref(cloneDeep(BookingModule.selectedBooking));

    return {
      t,
      isVisible,
      teamsArray,
      currentTeam,
      cardsIcon,
      closeIcon,
      successIcon,
      arrowDownIcon,
      isLoading,
      bookingId,
      waitForNfcTag,
      form,
      validateForm,
      nfcTag,
      cancelledTeams,
      bookingCopy,
      levelOptions,
      bookingToken,
      modules: [Navigation, Virtual, FreeMode],
      setSwiperRef,
    };
  },
  methods: {
    async close() {
      const isSameObj = isEqual(
        BookingModule.selectedBooking,
        this.bookingCopy
      );
      if (!isSameObj) {
        // TODO(order-migration): team pairing scans specific team tickets. The order
        // model has no team↔token mapping yet, so this scans all the order's active
        // tokens (approximation). Narrow to the paired team's tokens once available.
        await scanOrderTicketsBulk();
      }
      BookingModule.setBookingStep({
        bookingUUID: BookingModule.selectedBookingUUID,
        step: BookingStep.CompleteOrder,
      });
    },
    debouncedPair: debounce(async function () {
      this.isLoading = true;
      await this.pair();
      this.isLoading = false;
    }, 400),
    async scanAndPair() {
      LogService.info("User clicked to scan and pair");
      const isManualPairingTest =
        POSConfigModule.groupInfo && POSConfigModule.groupInfo.id === 16342;
      console.log("----- isManualPairingTest");
      console.log(isManualPairingTest);
      if (!isManualPairingTest) {
        const validName = await this.validateForm();
        if (!validName) {
          LogService.info("Team name not valid");
          return;
        }
      }

      this.isLoading = true;
      if (!isManualPairingTest) {
        const nfcTag: string = await this.waitForNfcTag();
        if (!nfcTag) {
          // nfcTag not available
          this.isLoading = false;
          LogService.info("No NFC Tag was provided");
          throw new Error("No NFC Tag was provided");
        }
        this.nfcTag = nfcTag;
        LogService.info(`Teams: received NFC tag: ${nfcTag}`);
      } else {
        this.nfcTag = "ABCDEFGH";
        LogService.info(`Teams: received NFC tag: ${this.nfcTag}`);
      }

      await this.debouncedPair();
    },
    async pair() {
      try {
        const team: ITeam = this.teamsArray[this.currentTeam];
        team.name = this.form.name.trim();
        team.teamLevel = this.form.level;

        // Reforamtting the NFC tag
        // Remove white spaces and convert to uppercase
        console.log(`nfcTag before format ${this.nfcTag}`);
        let formattedNfcTag = this.nfcTag.replace(/\s+/g, "").toUpperCase();
        console.log(`nfcTag after format ${formattedNfcTag}`);
        const isManualPairingTest =
          POSConfigModule.groupInfo && POSConfigModule.groupInfo.id === 16342;
        if (!isManualPairingTest) {
          // Check if the tag contains only 16 ASCII hexadecimal characters
          const isValidNfcTag = /[0-9A-F]+$/i.test(formattedNfcTag);
          if (!isValidNfcTag) {
            NotificationService.showCustomError(
              this.t("teams.invalid-tag").replace("xxxxxxx", formattedNfcTag)
            );
            LogService.info(`Invalid formatted nfcTag ${formattedNfcTag}`);
            console.log(`Invalid formatted nfcTag ${formattedNfcTag}`);
            return;
          }
        }

        team.nfcTag = formattedNfcTag;

        if (team.uuid) {
          LogService.info(
            `Teams: going to patch team with uuid: ${team.uuid}`,
            team
          );
          // this team aready exists so let's do a PUT
          const res = await patchPairTeam(
            team,
            this.bookingId,
            this.bookingToken
          );
          if (res && res.status === "success") {
            const teamData = res.data.teamOfParticipants;
            team.paired = true;
            team.uuid = teamData.uuid;
            LogService.info(
              `Teams: successfully updated team with uuid: ${team.uuid}`,
              team
            );

            // scan the tickets of this team
            this.scanTeamTickets(team.ticketUuids);
          } else if (res) {
            LogService.info(
              `Teams: error when patching team uuid: ${team.uuid}`,
              { params: { errorResponse: res, team: team } }
            );
            const error = new Error();
            error.message = res.data.errors[0];
            throw error;
          }
          return;
        }

        LogService.info(`Teams: going to POST create new team`, team);
        const res = await postPairTeam(team, this.bookingId, this.bookingToken);
        if (res && res.status === "success") {
          const teamData = res.data.teamOfParticipants;
          team.paired = true;
          team.uuid = teamData.uuid;
          LogService.info(
            `Teams: successfully created team with uuid: ${team.uuid}`,
            team
          );

          // scan the tickets of this team
          this.scanTeamTickets(team.ticketUuids);
        } else if (res) {
          LogService.info(
            `Teams: error when creating team uuid: ${team.uuid}`,
            { params: { errorResponse: res, team: team } }
          );
          const error = new Error();
          error.message = res.data.errors[0];
          throw error;
        }
      } catch (error: any) {
        console.log(error);
        if (
          error.message &&
          String(error.message).includes("The team with name")
        ) {
          NotificationService.showCustomError(this.t("teams.name-taken"));
        }

        LogService.info(`Teams: error when scanning/creating/patching team`, {
          params: { error: error, team: this.teamsArray[this.currentTeam] },
        });
      }
    },
    proceed() {
      if (this.currentTeam === this.teamsArray.length - 1) {
        // this is the last team let's close the UI
        this.close();
        return;
      }
      this.currentTeam++;
      this.form.name = this.teamsArray[this.currentTeam].name;
      this.form.level =
        this.teamsArray[this.currentTeam].teamLevel || TeamLevel.MEDIUM;
    },
    unpair() {
      this.teamsArray[this.currentTeam].paired = false;
    },
    mergedTeams(createdTeams: ITeam[], fetchedTeams: ITeam[]): ITeam[] {
      const linkedTeams: ITeam[] = [];
      for (const fetchedTeam of fetchedTeams) {
        // find a matching created team based on properties: startDateTime, endDateTime, roomPlayers
        const matchingCreatedTeamIndex = createdTeams.findIndex(
          (createdTeam) =>
            createdTeam.startDateTime === fetchedTeam.startDateTime &&
            createdTeam.endDateTime === fetchedTeam.endDateTime &&
            createdTeam.roomPlayers === fetchedTeam.roomPlayers
        );

        if (matchingCreatedTeamIndex !== -1) {
          // let's link the tickets of the created team to the fetched team
          fetchedTeam.ticketUuids =
            createdTeams[matchingCreatedTeamIndex].ticketUuids;
          // if a match is found, add the fetched team to the result
          linkedTeams.push(fetchedTeam);
          // remove the matched created team from the array to avoid duplicates
          createdTeams.splice(matchingCreatedTeamIndex, 1);
        } else {
          // this teams was already created, but now it's tickets are removed / changed
          // this teams should be cencelled
          this.cancelledTeams.push(fetchedTeam);
        }
      }
      linkedTeams.push(...createdTeams);
      return linkedTeams;
    },
    selectTeam(index: number) {
      this.currentTeam = index;
      this.form.name = this.teamsArray[index].name;
      this.form.level = this.teamsArray[index].teamLevel || TeamLevel.MEDIUM;
    },
    scanTeamTickets(pssUuids: string[]) {
      LogService.info(`Going to scan team's tickets, pssUuids: ${pssUuids}`);
      const booking: IProductSlotBooking = this.bookingCopy;
      const now = nowDB();
      for (let i = 0; i < booking.pssList.length; i++) {
        const pss = booking.pssList[i];
        if (
          pss.scanningStatus !== ScanningStatus.Scanned &&
          pssUuids.includes(String(pss.uuid))
        ) {
          pss.scanningStatus = ScanningStatus.Scanned;
          pss.scanningUserId = UserModule.userId;
          pss.updatedAt = now;
        }
      }
    },
  },
  /**
   * When the Teams vue loads
   * we pull the bookingId if we don't have it yet
   * we fetch all the existing teams
   * we create teams locally based on the booking's tickets
   * we compare and merge the fetched teams with the locally created teams
   * we cancel the extra teams that are attached to cancelled tickets
   */
  async mounted() {
    // attach the scanner
    LogService.info(`Scanner: going to attach scanner to Teams vue`);
    attachedVueElement();
    AppModule.updatedIsLoading(true);
    const booking = BookingModule.selectedBooking;
    // get the bookingId
    let bookingInfo;
    // repeat request 3 times
    let repeat = 0;
    try {
      while (repeat++ < 3) {
        bookingInfo = await getBookingWithUUID(
          BookingModule.selectedBookingUUID
        );
        if (bookingInfo != null) break;
        await Timeout.set(3000);
      }
      if (!bookingInfo || !bookingInfo.id || !bookingInfo.token) {
        this.close();
        throw new Error(
          `Booking with ${BookingModule.selectedBookingUUID} not synced`
        );
      }
      this.bookingId = bookingInfo.id;
      this.bookingToken = bookingInfo.token;
      const newTeams = await createTeams();
      const fetchedTeams = await fetchBookingTeams(this.bookingId);
      if (!fetchedTeams.length) {
        this.teamsArray = newTeams;
      } else {
        this.teamsArray = this.mergedTeams(newTeams, fetchedTeams);
        // let's send a delete request to cancel all the extra teams
        // an extra team is a team that is already on backend, but it's tickets are removed from the booking
        for (let i = 0; i < this.cancelledTeams.length; i++) {
          const team: ITeam = this.cancelledTeams[i];
          LogService.info(
            `Teams: going to cancel team with uuid: ${team.uuid}`
          );
          try {
            const res = await deleteTeam(
              String(team.uuid),
              Number(bookingInfo.id),
              bookingInfo.token
            );
          } catch (error) {
            LogService.info(
              `Teams: error when cancelling team with uuid: ${team.uuid}`
            );
          }
        }
      }
      this.form.name = this.teamsArray[0].name;
      this.form.level = this.teamsArray[0].teamLevel || TeamLevel.MEDIUM;
      AppModule.updatedIsLoading(false);
    } catch (error) {
      console.log(error);
      AppModule.updatedIsLoading(false);
    }
  },
  unmounted() {
    // detach the scanner
    detachVueElement();
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;
.teams-view-wrapper {
  position: absolute;
  width: calc(100% - 40px);
  height: calc(100% - 40px);
  z-index: 999;
  background-color: $color-light-grey4;
  font-size: 16px;

  .teams-container {
    position: relative;
  }
  .overlay {
    position: absolute;
    z-index: 1;
    background-color: rgba($color: #000000, $alpha: 0.4);
  }

  .close-button {
    position: absolute;
    right: 20px;
    top: 20px;
    width: 30px;
    height: 30px;
    background-color: #e5e5e5;
    z-index: 10;
  }

  .left {
    border-right: 2px solid #c3c3c3;
    .input-label {
      padding-bottom: 5px;
      span {
        color: #c9c9c9;
      }
    }
    .bottom {
      flex-grow: 1;
      padding: 20px;
      border-top: 2px solid #c3c3c3;
    }
    .buttons {
      gap: 10px;
      .button {
        margin: 0;
      }
    }
  }

  .right {
    position: relative;
    .content {
      position: absolute;
      top: 50px;
      left: 50px;
      width: calc(100% - 100px);
      height: calc(100% - 100px);
      z-index: 10;
      gap: 50px;
      flex-direction: column;
      background-color: $color-light-grey4;
    }
    .card-icon {
      position: relative;
    }
    .success-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
    .unpair-button {
      text-decoration: underline;
    }
  }

  .f-small {
    font-size: 12px;
  }
  .f-large {
    font-size: 32px;
  }
  .swiper-wrapper {
    background-color: $color-light-grey4;
    height: 20%;
    position: sticky;
    z-index: 11;
    .team {
      height: 56px;
      opacity: 0.4;
      cursor: pointer;
    }
    .selected {
      opacity: 1;
    }
    .swiper {
      width: 100%;
      padding-left: 50px;
      .swiper-button-prev,
      .swiper-button-next {
        opacity: 1;
        background-color: $color-light-grey4;
        z-index: 12;
      }
      .swiper-button-prev::after,
      .swiper-button-next::after {
        font-size: 25px;
        font-weight: 900;
        color: $color-black-2;
      }
      .swiper-button-prev {
        left: 0;
        box-shadow: 10px 0 15px -2px $color-light-grey4;
      }
      .swiper-button-next {
        right: 0;
        box-shadow: -10px 0 15px -2px $color-light-grey4;
      }
    }
  }
}
</style>
